import React, { useState } from 'react';
import '../styles/admin-seo-settings.css';

const BlogSeoFields = ({
  formData,
  imagePreview,
  onChange,
  onSlugGenerate,
  mode = 'page',
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewTab, setPreviewTab] = useState('google'); // 'google' | 'social'

  const titleLength = formData.title_tag ? formData.title_tag.length : 0;
  const descLength = formData.meta_description ? formData.meta_description.length : 0;

  // Counter badge status
  const getTitleStatus = (len) => {
    if (len === 0) return { text: '0 / 60', class: 'warning' };
    if (len >= 40 && len <= 60) return { text: `${len} / 60 Optimal`, class: 'optimal' };
    if (len > 60) return { text: `${len} / 60 Too Long`, class: 'excess' };
    return { text: `${len} / 60 (Short)`, class: 'warning' };
  };

  const getDescStatus = (len) => {
    if (len === 0) return { text: '0 / 160', class: 'warning' };
    if (len >= 120 && len <= 160) return { text: `${len} / 160 Optimal`, class: 'optimal' };
    if (len > 160) return { text: `${len} / 160 Too Long`, class: 'excess' };
    return { text: `${len} / 160 (Short)`, class: 'warning' };
  };

  const titleStatus = getTitleStatus(titleLength);
  const descStatus = getDescStatus(descLength);

  // Auto-fetch Fallbacks
  const previewTitle =
    formData.title_tag || formData.title || 'Blog Post Title - J P Home Services';
  const previewSlug = formData.url_title || 'blog-post-slug';
  const previewDesc =
    formData.meta_description ||
    'Search engines will display this summary in search results. Write an engaging meta description to boost click-through rates from Google.';

  // Social & Twitter Auto-Fetch
  const socialTitle =
    formData.og_title || formData.title_tag || formData.title || 'Blog Post Title';
  const socialDesc =
    formData.og_description ||
    formData.meta_description ||
    'Read this insightful blog post from J P Home Services.';
  const socialImage = imagePreview || '';

  return (
    <div className={`seo-card-container ${mode === 'modal' ? 'modal-mode' : ''}`}>
      <div className="seo-card-header">
        <div className="seo-header-title-wrap">
          <div className="seo-header-icon">🔍</div>
          <div>
            <h3 className="seo-header-title">SEO & Meta Settings</h3>
            <p className="seo-header-subtitle">
              Optimize search rankings, SERP snippets, Open Graph & Twitter Cards
            </p>
          </div>
        </div>
        <span className="seo-badge-tag">SEO Configuration</span>
      </div>

      {/* Auto-fetch notification banner */}
      <div className="auto-fetch-banner">
        <span className="auto-fetch-badge-tag">⚡ AUTO-SYNC ACTIVE</span>
        <span>
          <strong>Open Graph & Twitter tags</strong> are automatically fetched from{' '}
          <strong>Meta Title</strong>, <strong>Meta Description</strong>, and{' '}
          <strong>Featured Image</strong>!
        </span>
      </div>

      {/* Preview Tabs */}
      <div className="serp-preview-wrapper">
        <div className="preview-tabs-bar">
          <button
            type="button"
            className={`preview-tab-btn ${previewTab === 'google' ? 'active' : ''}`}
            onClick={() => setPreviewTab('google')}
          >
            <span>🌐</span> Google Search (SERP)
          </button>
          <button
            type="button"
            className={`preview-tab-btn ${previewTab === 'social' ? 'active' : ''}`}
            onClick={() => setPreviewTab('social')}
          >
            <span>📱</span> Social & Twitter Card Preview
          </button>
        </div>

        {previewTab === 'google' ? (
          /* Google SERP Preview */
          <div className="serp-preview-box">
            <div className="serp-url-row">
              <span className="serp-favicon">🌐</span>
              <span className="serp-site-name">J P Home Services</span>
              <span className="serp-breadcrumb">› blog › {previewSlug}</span>
            </div>
            <div className="serp-title-link">{previewTitle}</div>
            <p className="serp-snippet">{previewDesc}</p>
          </div>
        ) : (
          /* Social & Twitter Card Preview */
          <div className="social-preview-card">
            <div className="social-preview-image-wrap">
              {socialImage ? (
                <img
                  src={socialImage}
                  alt="Social preview"
                  className="social-preview-img"
                />
              ) : (
                <div className="social-preview-placeholder">
                  <span>🖼️</span>
                  <span>Featured image will appear here</span>
                </div>
              )}
            </div>
            <div className="social-preview-body">
              <div className="social-preview-domain">JPHOMESERVICE.IN</div>
              <div className="social-preview-title">{socialTitle}</div>
              <p className="social-preview-desc">{socialDesc}</p>
            </div>
          </div>
        )}
      </div>

      {/* Main SEO Inputs */}
      <div className="seo-fields-grid">
        {/* Meta Title */}
        <div className="seo-field-group">
          <div className="seo-field-label-wrap">
            <label htmlFor="title_tag" className="seo-field-label">
              <span>🏷️</span> Meta Title (Title Tag)
            </label>
            <span className={`seo-counter-pill ${titleStatus.class}`}>
              {titleStatus.text}
            </span>
          </div>
          <input
            type="text"
            id="title_tag"
            name="title_tag"
            className="seo-input"
            value={formData.title_tag || ''}
            onChange={onChange}
            placeholder="e.g. Best Maid Services in Ahmedabad | J P Home Services"
          />
          <span className="seo-help-text">
            Recommended 50–60 characters. Also auto-used for Open Graph & Twitter title.
          </span>
        </div>

        {/* URL Slug */}
        <div className="seo-field-group">
          <div className="seo-field-label-wrap">
            <label htmlFor="url_title" className="seo-field-label">
              <span>🔗</span> URL Slug (Permalink)
            </label>
          </div>
          <div className="seo-slug-container">
            <span className="seo-slug-prefix">https://jphomeservice.in/blog/</span>
            <input
              type="text"
              id="url_title"
              name="url_title"
              className="seo-slug-input"
              value={formData.url_title || ''}
              onChange={onChange}
              placeholder="best-maid-services-in-ahmedabad"
            />
            {onSlugGenerate && (
              <button
                type="button"
                className="seo-slug-btn"
                onClick={onSlugGenerate}
                title="Generate URL slug from Title"
              >
                ⚡ Generate
              </button>
            )}
          </div>
          <span className="seo-help-text">
            The URL structure for this blog post. Use lowercase letters, numbers, and hyphens.
          </span>
        </div>

        {/* Meta Description */}
        <div className="seo-field-group">
          <div className="seo-field-label-wrap">
            <label htmlFor="meta_description" className="seo-field-label">
              <span>📝</span> Meta Description
            </label>
            <span className={`seo-counter-pill ${descStatus.class}`}>
              {descStatus.text}
            </span>
          </div>
          <textarea
            id="meta_description"
            name="meta_description"
            className="seo-textarea"
            rows="3"
            value={formData.meta_description || ''}
            onChange={onChange}
            placeholder="Provide a concise summary of your blog post that encourages searchers to click..."
          />
          <span className="seo-help-text">
            Recommended 150–160 characters. Also auto-used for Open Graph & Twitter description.
          </span>
        </div>

        {/* Row 2: Focus Keyword & Meta Keywords */}
        <div className="seo-fields-row-2">
          <div className="seo-field-group">
            <label htmlFor="focus_keyword" className="seo-field-label">
              <span>🎯</span> Focus / Target Keyword
            </label>
            <input
              type="text"
              id="focus_keyword"
              name="focus_keyword"
              className="seo-input"
              value={formData.focus_keyword || ''}
              onChange={onChange}
              placeholder="e.g. maid services in ahmedabad"
            />
            <span className="seo-help-text">Primary search term to target for ranking.</span>
          </div>

          <div className="seo-field-group">
            <label htmlFor="keywords" className="seo-field-label">
              <span>🔑</span> Meta Keywords
            </label>
            <input
              type="text"
              id="keywords"
              name="keywords"
              className="seo-input"
              value={formData.keywords || ''}
              onChange={onChange}
              placeholder="e.g. maid services, housekeeping, cleaner ahmedabad"
            />
            <span className="seo-help-text">Separate keywords or phrases with commas.</span>
          </div>
        </div>

        {/* Row 3: Canonical URL & Robots Meta */}
        <div className="seo-fields-row-2">
          <div className="seo-field-group">
            <label htmlFor="canonical_url" className="seo-field-label">
              <span>🌐</span> Canonical URL
            </label>
            <input
              type="url"
              id="canonical_url"
              name="canonical_url"
              className="seo-input"
              value={formData.canonical_url || ''}
              onChange={onChange}
              placeholder="e.g. https://jphomeservice.in/blog/your-slug"
            />
            <span className="seo-help-text">
              Leave blank to use default self-referencing canonical URL.
            </span>
          </div>

          <div className="seo-field-group">
            <label htmlFor="meta_robots" className="seo-field-label">
              <span>🤖</span> Robots Meta Tag (Indexing)
            </label>
            <select
              id="meta_robots"
              name="meta_robots"
              className="seo-select"
              value={formData.meta_robots || 'index, follow'}
              onChange={onChange}
            >
              <option value="index, follow">Index, Follow (Recommended)</option>
              <option value="noindex, follow">Noindex, Follow</option>
              <option value="index, nofollow">Index, Nofollow</option>
              <option value="noindex, nofollow">Noindex, Nofollow</option>
            </select>
            <span className="seo-help-text">Instructions for search engine spiders/crawlers.</span>
          </div>
        </div>

        {/* Advanced / Social Open Graph & Twitter Custom Overrides Toggle */}
        <div
          className="seo-advanced-toggle"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <span>🌐 Custom Social & Twitter Overrides (Optional)</span>
          <span>{showAdvanced ? '▲ Hide' : '▼ Show'}</span>
        </div>

        {showAdvanced && (
          <div className="seo-advanced-content">
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              💡 <em>Note: If left empty, these fields automatically use Meta Title, Meta Description, and Featured Image.</em>
            </div>

            <div className="seo-field-group">
              <label htmlFor="og_title" className="seo-field-label">
                <span>📱</span> Custom OG & Twitter Title
              </label>
              <input
                type="text"
                id="og_title"
                name="og_title"
                className="seo-input"
                value={formData.og_title || ''}
                onChange={onChange}
                placeholder={`Auto-fetched: ${formData.title_tag || formData.title || 'Meta Title'}`}
              />
              <span className="seo-help-text">
                Leave empty to automatically use Meta Title.
              </span>
            </div>

            <div className="seo-field-group">
              <label htmlFor="og_description" className="seo-field-label">
                <span>💬</span> Custom OG & Twitter Description
              </label>
              <textarea
                id="og_description"
                name="og_description"
                className="seo-textarea"
                rows="2"
                value={formData.og_description || ''}
                onChange={onChange}
                placeholder={`Auto-fetched: ${formData.meta_description ? formData.meta_description.slice(0, 80) + '...' : 'Meta Description'}`}
              />
              <span className="seo-help-text">
                Leave empty to automatically use Meta Description.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSeoFields;
